import { normalizeForSearch, searchMatcher } from "utils/search";

import type { DirectoryAttendee, Signal } from "./schemas";
import type { ResolvedSignal } from "./signal-utils";

/**
 * What the attendee directory is narrowed by. Every filter is ANDed with the
 * others. The filter panel offers one signal at a time, so `signalIds` holds
 * one id or none there; inside the list a person matches when they picked ANY
 * of the ids, because "picked all of A, B and C" would empty the list at once
 * with only three picks per person. A job title is not a filter: the text
 * search matches it.
 */
export interface DirectoryFilters {
  query: string;
  signalIds: string[];
  company: string;
  trackId: string;
  sameSignalsAsMe: boolean;
  sameTrackAsMe: boolean;
}

export const NO_FILTERS: DirectoryFilters = {
  query: "",
  signalIds: [],
  company: "",
  trackId: "",
  sameSignalsAsMe: false,
  sameTrackAsMe: false,
};

/** The signed-in member as the directory sees them. */
export interface DirectoryViewer {
  attendeeId?: string;
  signalIds: string[];
  trackId?: string;
}

/**
 * How many filters other than the text search are on. The signal dropdown is
 * one filter however many ids it holds.
 */
export function activeFilterCount(filters: DirectoryFilters): number {
  return (
    (filters.signalIds.length > 0 ? 1 : 0) +
    (filters.company ? 1 : 0) +
    (filters.trackId ? 1 : 0) +
    (filters.sameSignalsAsMe ? 1 : 0) +
    (filters.sameTrackAsMe ? 1 : 0)
  );
}

/**
 * The distinct non-empty values of one field, for a filter dropdown.
 *
 * Company is free text typed by organisers and by people on their own
 * profile, so "Holtzbrinck" and "holtzbrinck " are one choice. The spelling
 * kept is the most common one, so the list reads the way most rows do; ties
 * go to the first seen.
 */
export function distinctValues(
  attendees: readonly DirectoryAttendee[],
  field: "company",
): string[] {
  const spellings = new Map<string, Map<string, number>>();
  for (const attendee of attendees) {
    const value = attendee[field].trim();
    if (!value) continue;
    const key = normalizeForSearch(value);
    const counts = spellings.get(key) ?? new Map<string, number>();
    counts.set(value, (counts.get(value) ?? 0) + 1);
    spellings.set(key, counts);
  }
  return [...spellings.values()]
    .map((counts) => {
      let best = "";
      let bestCount = 0;
      for (const [value, count] of counts) {
        if (count > bestCount) {
          best = value;
          bestCount = count;
        }
      }
      return best;
    })
    .sort((a, b) => a.localeCompare(b));
}

function sameText(a: string, b: string): boolean {
  return normalizeForSearch(a.trim()) === normalizeForSearch(b.trim());
}

/**
 * `filters` without the signal ids that are not on the event's list any more.
 *
 * An organiser can delete a signal while somebody has it chosen. The dropdown
 * has no option for it, so it would show "All signals" while the list stayed
 * narrowed and the count stayed on. Returns the same object when nothing was
 * dropped, so a memo keyed on it holds.
 */
export function withoutMissingSignals(
  filters: DirectoryFilters,
  signals: readonly Signal[],
): DirectoryFilters {
  if (filters.signalIds.length === 0) return filters;
  const live = new Set(signals.map((signal) => signal.id));
  const signalIds = filters.signalIds.filter((id) => live.has(id));
  return signalIds.length === filters.signalIds.length
    ? filters
    : { ...filters, signalIds };
}

/**
 * The directory narrowed by `filters`, in directory order, except that "same
 * signals as me" lists the people who share the most picks first.
 *
 * The viewer never appears in their own "same as me" results: the point is who
 * else. `signalsByAttendee` is the resolved join from `useSignalsByAttendee`,
 * so a pick whose signal was deleted never matches anything.
 */
export function filterDirectory(
  attendees: readonly DirectoryAttendee[],
  filters: DirectoryFilters,
  signalsByAttendee: ReadonlyMap<string, readonly ResolvedSignal[]>,
  viewer: DirectoryViewer,
): DirectoryAttendee[] {
  const matches = searchMatcher(filters.query);
  const wanted = new Set(filters.signalIds);
  const mine = new Set(viewer.signalIds);
  const sharedCount = (attendee: DirectoryAttendee) =>
    (signalsByAttendee.get(attendee.id) ?? []).filter((signal) =>
      mine.has(signal.id),
    ).length;

  const result = attendees.filter((attendee) => {
    if (!matches([attendee.name, attendee.role, attendee.company])) {
      return false;
    }
    if (filters.company && !sameText(attendee.company, filters.company)) {
      return false;
    }
    if (filters.trackId && attendee.trackId !== filters.trackId) {
      return false;
    }
    if (wanted.size > 0) {
      const picks = signalsByAttendee.get(attendee.id) ?? [];
      if (!picks.some((signal) => wanted.has(signal.id))) return false;
    }
    const isViewer =
      viewer.attendeeId !== undefined && attendee.id === viewer.attendeeId;
    if (filters.sameSignalsAsMe) {
      if (isViewer || mine.size === 0 || sharedCount(attendee) === 0) {
        return false;
      }
    }
    if (filters.sameTrackAsMe) {
      if (isViewer || !viewer.trackId || attendee.trackId !== viewer.trackId) {
        return false;
      }
    }
    return true;
  });

  if (!filters.sameSignalsAsMe) return result;
  // Array.prototype.sort is stable, so equal counts keep directory order.
  return result
    .map((attendee) => ({ attendee, shared: sharedCount(attendee) }))
    .sort((a, b) => b.shared - a.shared)
    .map(({ attendee }) => attendee);
}

export interface SignalSummary {
  signal: Signal;
  /** Directory entries that picked this signal, in directory order. */
  attendees: DirectoryAttendee[];
}

/**
 * Every signal of the event with the people on the directory who picked it,
 * in the organiser's signal order.
 *
 * Counted over the directory, not over the selections collection: a selection
 * can outlive its person's place on the list (a withdrawn attendee keeps the
 * document), and the count must agree with the list a tap opens.
 */
export function summariseSignals(
  signals: readonly Signal[],
  attendees: readonly DirectoryAttendee[],
  signalsByAttendee: ReadonlyMap<string, readonly ResolvedSignal[]>,
): SignalSummary[] {
  const bySignal = new Map<string, DirectoryAttendee[]>(
    signals.map((signal) => [signal.id, []]),
  );
  for (const attendee of attendees) {
    for (const pick of signalsByAttendee.get(attendee.id) ?? []) {
      bySignal.get(pick.id)?.push(attendee);
    }
  }
  return signals.map((signal) => ({
    signal,
    attendees: bySignal.get(signal.id) ?? [],
  }));
}
